import React from 'react'
import styled, { css } from 'styled-components'
import { media } from 'src/styles/styled-components'
import PropTypes from 'prop-types'
import { generateScProps, deviceInfoProvider, generateJSX } from 'src/utils'

// ArComponents
import ArAnimation from 'src/components/ArAnimation/ArAnimation'
import ArBaseImage from 'src/components/ArBaseImage/ArBaseImage'
import ArBaseButton from 'src/components/ArBaseButton/ArBaseButton'
import ArBaseContainer from 'src/components/ArBaseContainer/ArBaseContainer'
import ArTextWithImageHeading from 'src/components/typography/ArTextWithImageHeading'
import ArTextWithImageDescription from 'src/components/typography/ArTextWithImageDescription'

const StyledWrapper = styled.section`
  background-color: ${ p => (p.scColors && p.scColors[0]) || 'transparent' };
  color: ${ p => (p.scColors && p.scColors[1]) || p.theme.colors.black };
  margin-bottom: ${ p => p.scComponentMargins && p.scComponentMargins.bottom[0] };
  margin-top: ${ p => p.scComponentMargins && p.scComponentMargins.top[0] };
  overflow: hidden;
  padding-bottom: ${ p => p.scComponentPaddings && p.scComponentPaddings.bottom[0] };
  padding-top: ${ p => p.scComponentPaddings && p.scComponentPaddings.top[0] };

  ${ media.tablet`
    margin-bottom: ${ p => p.scComponentMargins && p.scComponentMargins.bottom[1] };
    padding-bottom: ${ p => p.scComponentPaddings && p.scComponentPaddings.bottom[1] };
    margin-top: ${ p => p.scComponentMargins && p.scComponentMargins.top[1] };
    padding-top: ${ p => p.scComponentPaddings && p.scComponentPaddings.top[1] };
  ` }

  ${ media.desktop`
    margin-bottom: ${ p => p.scComponentMargins && p.scComponentMargins.bottom[2] };
    padding-bottom: ${ p => p.scComponentPaddings && p.scComponentPaddings.bottom[2] };
    margin-top: ${ p => p.scComponentMargins && p.scComponentMargins.top[2] };
    padding-top: ${ p => p.scComponentPaddings && p.scComponentPaddings.top[2] };
  ` }
`

const StyledContainer = styled(ArBaseContainer)`

  ${ media.tablet`
    display: flex;
    align-items: center;
    flex-direction: ${ p => p.scImageToRight && 'row-reverse' };
  ` }
`

const StyledImage = styled(ArBaseImage)`
  margin-left: ${ p => p.scImageMargins && p.scImageMargins.left[0] };
  margin-bottom: ${ p => p.scImageMargins && p.scImageMargins.bottom[0] };
  margin-right: ${ p => p.scImageMargins && p.scImageMargins.right[0] };
  width: auto;

  ${ media.tablet`
    margin-left: ${ p => p.scImageMargins && p.scImageMargins.left[1] };
    margin-bottom: ${ p => p.scImageMargins && p.scImageMargins.bottom[1] };
    margin-right: ${ p => p.scImageMargins && p.scImageMargins.right[1] };
    width: ${ p => p.scImageWidth && (p.scImageWidth[1] / p.theme.percentageFactorTablet * 100 + '%') };
    flex-shrink: 0;
  ` }

  ${ media.desktop`
    margin-left: ${ p => p.scImageMargins && p.scImageMargins.left[2] };
    margin-bottom: ${ p => p.scImageMargins && p.scImageMargins.bottom[2] };
    margin-right: ${ p => p.scImageMargins && p.scImageMargins.right[2] };
    width: ${ p => p.scImageWidth && (p.scImageWidth[2] / p.theme.percentageFactorDesktop * 100 + '%') };
  ` }
`

const StyledTextWrapper = styled.div`
  padding-bottom: ${ p => p.scTextWrapperPaddings && p.scTextWrapperPaddings.bottom[0] };
  padding-top: ${ p => p.scTextWrapperPaddings && p.scTextWrapperPaddings.top[0] };

  ${ media.tablet`
    padding-bottom: ${ p => p.scTextWrapperPaddings && p.scTextWrapperPaddings.bottom[1] };
    padding-top: ${ p => p.scTextWrapperPaddings && p.scTextWrapperPaddings.top[1] };
    margin-right: ${ p => p.scImageToRight && p.scGap && (p.scGap[1] / p.theme.percentageFactorTablet * 100 + '%') };
    margin-left: ${ p => !p.scImageToRight && p.scGap && (p.scGap[1] / p.theme.percentageFactorTablet * 100 + '%') };
  ` }

  ${ media.desktop`
    padding-bottom: ${ p => p.scTextWrapperPaddings && p.scTextWrapperPaddings.bottom[2] };
    padding-top: ${ p => p.scTextWrapperPaddings && p.scTextWrapperPaddings.top[2] };
    margin-right: ${ p => p.scImageToRight && p.scGap && (p.scGap[2] / p.theme.percentageFactorDesktop * 100 + '%') };
    margin-left: ${ p => !p.scImageToRight && p.scGap && (p.scGap[2] / p.theme.percentageFactorDesktop * 100 + '%') };
  ` }
`

const StyledTitle = styled(ArTextWithImageHeading)`
  margin-bottom: ${ p => p.scTitleBottomMargins && p.scTitleBottomMargins[0] };
  text-align: center;
  max-width: ${ p => p.scTitleMaxWidth && p.scTitleMaxWidth[0] };

  ${ media.tablet`
    margin-bottom: ${ p => p.scTitleBottomMargins && p.scTitleBottomMargins[1] };
    text-align: left;
    max-width: ${ p => p.scTitleMaxWidth && p.scTitleMaxWidth[1] };
  ` }

  ${ media.desktop`
    margin-bottom: ${ p => p.scTitleBottomMargins && p.scTitleBottomMargins[2] };
    max-width: ${ p => p.scTitleMaxWidth && p.scTitleMaxWidth[2] };
  ` }
`

const StyledDescription = styled(ArTextWithImageDescription)`
  text-align: center;

  ${ media.tablet`
    text-align: left;
  ` }
`

const StyledButton = styled(ArBaseButton)`
  margin: 30px auto 0;

  ${ media.tablet`
    margin-left: 0;
  ` }
`

class ArTextWithImageHalvedOnTablet extends React.Component {
  static propTypes = {
    image: PropTypes.object,
    title: PropTypes.oneOfType([ PropTypes.string, PropTypes.array ]),
    description: PropTypes.oneOfType([ PropTypes.string, PropTypes.array ]),
    scImageToRight: PropTypes.bool,
    scImageWidth: PropTypes.array, // [ 'mobile', 'tablet', 'desktop' ]
    scImageMargins: PropTypes.shape({
      right: PropTypes.arrayOf(PropTypes.string), // [ 'mobile', 'tablet', 'desktop' ]
      bottom: PropTypes.arrayOf(PropTypes.string),
      left: PropTypes.arrayOf(PropTypes.string),
    }),
    scComponentMargins: PropTypes.shape({
      top: PropTypes.arrayOf(PropTypes.string),
      bottom: PropTypes.arrayOf(PropTypes.string),
    }),
    scComponentPaddings: PropTypes.shape({
      top: PropTypes.arrayOf(PropTypes.string),
      bottom: PropTypes.arrayOf(PropTypes.string),
    }),
    scTextWrapperPaddings: PropTypes.shape({
      top: PropTypes.arrayOf(PropTypes.string),
      bottom: PropTypes.arrayOf(PropTypes.string),
    }),
    scColors: PropTypes.arrayOf(PropTypes.string), // [backgroundColor, textColor]
    scGap: PropTypes.arrayOf(PropTypes.number), // ['mobile', 'tablet', 'desktop']
    scTitleBottomMargins: PropTypes.arrayOf(PropTypes.string), // ['mobile', 'tablet', 'desktop']
    scTitleMaxWidth: PropTypes.arrayOf(PropTypes.string), // ['mobile', 'tablet', 'desktop']
    scHideMobileImg: PropTypes.bool,
  }

  get hideImage () {
    return this.props.device.isMobile && this.props.scHideMobileImg
  }

  render () {
    const { image, title, description, animation, cta } = this.props
    const scProps = generateScProps(this.props)
    return (
      <StyledWrapper {...scProps}>
        <StyledContainer {...scProps}>
          { !this.hideImage && image && <StyledImage src={image.url} alt={image.alt} scFit="contain" {...scProps}></StyledImage>}
          { animation && <ArAnimation animation={animation} />}
          <StyledTextWrapper {...scProps}>
            <StyledTitle {...scProps}>{ generateJSX(title) }</StyledTitle>
            <StyledDescription {...scProps}>{ generateJSX(description) }</StyledDescription>
            {cta && <StyledButton
              variant="violet"
              label={cta.label}
              to={cta.url}
            />}
          </StyledTextWrapper>
        </StyledContainer>
      </StyledWrapper>
    )
  }
}

export default deviceInfoProvider(ArTextWithImageHalvedOnTablet)
